import React, { useContext, useState, useEffect } from "react";
import {
  Drawer,
  Modal,
  Row,
  Col,
  Typography,
  Form,
  Input,
  Button,
  DatePicker,
  message,
  InputNumber,
} from "antd";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import { CloseOutlined } from "@ant-design/icons";
import inputStyles from "../../../../styles/Custom/Input.module.css";
import { validateConfig } from "../../../../config/validate.config";
import { UserContext } from "../../../../context/UserContext";
import { assignIpServicesApi } from "../../../../services/receptionist.services";
import useQuery from "../../../../hooks/useQuery";
import dayjs from "dayjs";
import { getCookie } from "../../../../helpers/localStorage";
import { FacilityID } from "../../../../constants/defaultKeys";
import styles from "./AssignServiceModal.module.css";

const MOBILE_MQ = "(max-width: 768px)";

const alignAssignServiceDatePickerBelow = (open) => {
  if (!open) return;

  const position = () => {
    const dropdown = document.querySelector(
      ".assign-service-date-picker-dropdown.ant-picker-dropdown"
    );
    const trigger =
      document.querySelector(
        ".assign-service-date-picker.ant-picker-focused"
      ) || document.querySelector(".assign-service-date-picker");

    if (!dropdown || !trigger) return;

    const rect = trigger.getBoundingClientRect();
    const panel = dropdown.querySelector(".ant-picker-panel-container");
    const panelHeight = panel?.offsetHeight || 320;
    const gap = 8;
    const viewportPad = 12;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPad;
    const spaceAbove = rect.top - viewportPad;
    const placeAbove =
      spaceBelow < panelHeight && spaceAbove > spaceBelow;

    const top = placeAbove
      ? Math.max(viewportPad, rect.top - panelHeight - gap)
      : Math.min(
          rect.bottom + gap,
          Math.max(viewportPad, window.innerHeight - panelHeight - viewportPad)
        );

    // Center horizontally in the viewport so the panel never clips off-screen
    // (Ant Design left alignment often overflows past the modal edge on mobile).
    dropdown.style.setProperty("position", "fixed", "important");
    dropdown.style.setProperty("top", `${top}px`, "important");
    dropdown.style.setProperty("left", "50%", "important");
    dropdown.style.setProperty("right", "auto", "important");
    dropdown.style.setProperty("bottom", "auto", "important");
    dropdown.style.setProperty("transform", "translateX(-50%)", "important");
    dropdown.style.setProperty(
      "width",
      `min(360px, calc(100vw - ${viewportPad * 2}px))`,
      "important"
    );
    dropdown.style.setProperty(
      "max-width",
      `calc(100vw - ${viewportPad * 2}px)`,
      "important"
    );
    dropdown.style.setProperty("z-index", "1600", "important");
  };

  requestAnimationFrame(position);
  window.setTimeout(position, 0);
  window.setTimeout(position, 50);
  window.setTimeout(position, 150);
};

const AssignServiceModal = ({ open, setOpen, editData, refresh }) => {
  const params = useQuery();
  const [form] = Form.useForm();
  const { userDetails } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(MOBILE_MQ).matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const onFinish = (values) => {
    setLoading(true);
    assignIpServicesApi(
      params?.patient,
      `facility=${Number(getCookie(FacilityID)) || userDetails?.currFacility}`,
      {
        ...values,
        service: editData?.id,
        from_date: dayjs(values?.from_date).format("YYYY-MM-DD"),
        till_date: dayjs(values?.till_date).format("YYYY-MM-DD"),
      }
    )
      .then((res) => {
        message.success(res?.data?.message);
        setLoading(false);
        setOpen(false);
        refresh();
      })
      .catch((err) => {
        message.error(err?.response?.data?.message);
        setLoading(false);
      });
  };

  const mobilePickerProps = isMobile
    ? {
        placement: "bottomLeft",
        getPopupContainer: () => document.body,
        popupClassName:
          "assign-service-date-picker-dropdown curengo-mobile-picker",
        inputReadOnly: true,
        onOpenChange: (pickerOpen) => {
          if (pickerOpen) {
            alignAssignServiceDatePickerBelow(pickerOpen);
          }
        },
      }
    : {
        placement: "bottomLeft",
        getPopupContainer: () => document.body,
      };

  const footerActions = (
    <div
      className="flex-end"
      style={{ gap: isMobile ? 10 : 24, flexWrap: "nowrap" }}
    >
      <Button
        type="primary"
        ghost
        className={btnStyles.mdBtn}
        onClick={() => setOpen(false)}
      >
        Cancel
      </Button>
      <Button
        loading={loading}
        type="primary"
        className={btnStyles.mdBtn}
        onClick={() => form.submit()}
      >
        Save
      </Button>
    </div>
  );

  const assignForm = (
    <Form
      layout="vertical"
      onFinish={onFinish}
      form={form}
      initialValues={{
        quantity: 1,
        from_date: dayjs(),
        till_date: dayjs(),
      }}
    >
      <Form.Item
        label="Number of times"
        name="quantity"
        rules={validateConfig.isRequired("No.Of Times")}
      >
        <InputNumber
          defaultValue={1}
          className={inputStyles.mdNumberInput}
          style={{ width: isMobile ? "100%" : "300px" }}
          placeholder="No of times"
        />
      </Form.Item>
      <Form.Item
        label="From"
        name="from_date"
        rules={validateConfig.isRequired("From Date")}
      >
        <DatePicker
          format="YYYY-MM-DD"
          className={`${inputStyles.mdDateInput} assign-service-date-picker`}
          style={{ width: isMobile ? "100%" : "300px" }}
          {...mobilePickerProps}
        />
      </Form.Item>
      <Form.Item
        label="To"
        name="till_date"
        rules={validateConfig.isRequired("To Date")}
      >
        <DatePicker
          format="YYYY-MM-DD"
          className={`${inputStyles.mdDateInput} assign-service-date-picker`}
          style={{ width: isMobile ? "100%" : "300px" }}
          {...mobilePickerProps}
        />
      </Form.Item>
      <Form.Item label="Info" name="comments">
        <Input.TextArea
          placeholder="Enter Info"
          className={inputStyles.mdInput}
          style={{ width: isMobile ? "100%" : "300px" }}
          rows={isMobile ? 3 : undefined}
        />
      </Form.Item>
    </Form>
  );

  if (isMobile) {
    return (
      <Modal
        title={editData ? "Assign Service" : "Add Patient"}
        open={open}
        onCancel={() => setOpen(false)}
        footer={footerActions}
        destroyOnClose
        centered={false}
        width={420}
        maskClosable={false}
        wrapClassName={styles.assignServiceModalWrap}
        styles={{
          body: { padding: 0 },
        }}
      >
        {assignForm}
      </Modal>
    );
  }

  return (
    <div>
      <Drawer
        width="30%"
        open={open}
        title={
          <Row>
            <Col span={12} className="flex-start">
              <Typography
                style={{
                  marginLeft: "12px",
                  fontWeight: "400",
                  fontSize: "20px",
                }}
              >
                {editData ? "Assign Service" : "Add Patient"}
              </Typography>
            </Col>
            <Col span={12} className="flex-end">
              <CloseOutlined onClick={() => setOpen(false)} />
            </Col>
          </Row>
        }
        footer={footerActions}
        onClose={() => setOpen(false)}
        closeIcon={null}
      >
        {assignForm}
      </Drawer>
    </div>
  );
};

export default AssignServiceModal;
